<template>
  <jd-tooltip
    ref="tooltipRef"
    v-bind="$attrs"
    :trigger="trigger"
    :placement="placement"
    :disabled="disabled"
    :visible="visible"
    :transition="transition"
    :popper-options="popperOptions"
    :tabindex="tabindex"
    :content="content"
    :offset="offset"
    :show-after="showAfter"
    :hide-after="hideAfter"
    :auto-close="autoClose"
    :show-arrow="showArrow"
    :aria-label="title"
    :effect="effect"
    :enterable="enterable"
    :popper-class="kls"
    :popper-style="style"
    :teleported="teleported"
    :persistent="persistent"
    :gpu-acceleration="gpuAcceleration"
    :append-to="appendTo"
    :fallback-placements="fallbackPlacements"
    @update:visible="onUpdateVisible"
    @before-show="beforeEnter"
    @before-hide="beforeLeave"
    @show="afterEnter"
    @hide="afterLeave"
  >
    <template v-if="$slots.reference">
      <slot name="reference" />
    </template>

    <template #content>
      <div
        data-component-name="Popover"
        data-event-name="close"
        data-event-content="关闭弹出框"
      >
        <div v-if="title" :class="ns.e('title')" role="title">
          {{ title }}
        </div>
        <slot>
          {{ content }}
        </slot>
      </div>
    </template>
  </jd-tooltip>
</template>
<script lang="ts" setup>
import { computed, provide, ref, unref } from 'vue'
import { JdTooltip } from '@jdesign-vue/components/tooltip'
import { addUnit } from '@jdesign-vue/utils'
import { useNamespace } from '@jdesign-vue/hooks'
import { popoverEmits, popoverProps } from './popover'
import type { TooltipInstance } from '@jdesign-vue/components/tooltip'

defineOptions({
  name: 'JdPopover',
})

const props = defineProps(popoverProps)
const emit = defineEmits(popoverEmits)

provide('jdTooltipDisableTrack', true)

const updateEventKeyRaw = `onUpdate:visible` as const

const onUpdateVisible = computed(() => {
  return props[updateEventKeyRaw]
})

const ns = useNamespace('popover')
const tooltipRef = ref<TooltipInstance>()
const popperRef = computed(() => {
  return unref(tooltipRef)?.popperRef
})

const style = computed(() => {
  return [
    {
      width: addUnit(props.width),
    },
    props.popperStyle!,
  ]
})

const kls = computed(() => {
  return [ns.b(), props.popperClass!, { [ns.m('plain')]: !!props.content }]
})

const gpuAcceleration = computed(() => {
  return props.transition === `${ns.namespace.value}-fade-in-linear`
})

const dispatchTrack = (eventName: string) => {
  if (typeof document === 'undefined') return
  document.dispatchEvent(
    new CustomEvent('customTrack', {
      detail: {
        'data-component-name': 'Popover',
        'data-event-name': eventName,
        'data-event-content': props.title || props.content || '弹出框',
      },
    })
  )
}

const hide = () => {
  tooltipRef.value?.hide()
}

const beforeEnter = () => {
  emit('before-enter')
}
const beforeLeave = () => {
  emit('before-leave')
}

const afterEnter = () => {
  if (props.trigger === 'hover') {
    dispatchTrack('hover')
  } else {
    dispatchTrack('open')
  }
  emit('after-enter')
}

const afterLeave = () => {
  if (props.trigger !== 'hover') {
    dispatchTrack('close')
  }
  emit('update:visible', false)
  emit('after-leave')
}

defineExpose({
  /** @description popper ref */
  popperRef,
  /** @description hide popover */
  hide,
})
</script>
